import { Input } from "antd";
import dayjs, { Dayjs } from "dayjs";
import now from "lodash/now";

import { ZoneTZIndependant } from "../../../services/TimeService/TimeService";
import { type DisabledTime } from "../../atoms/RVDatePickerDayJSFacade/utils/DisabledTimes/DisabledTimes";
import { RVDateTimePicker } from "../../atoms/RVDateTimePicker/RVDateTimePicker";
import { RVFormItem } from "../../atoms/RVFormItem/RVFormItem";
import {
    RVFormFloatingLabelWrap,
    type RVFormFloatingLabelWrapProps,
} from "../RVFormFloatingLabelWrap/RVFormFloatingLabelWrap";

type RVEnhancedDateTimePickerFormItemProps = RVFormFloatingLabelWrapProps & {
    timezone: ZoneTZIndependant;
    onChange?: (value: any) => void;
    useUTCValue?: boolean;
    disabledDate?: (date: Dayjs) => boolean;
    disabledTime?: DisabledTime<Dayjs>;
};

const validateDateHasNotPassed = (_: any, date: Date) => {
    if (!date) {
        return Promise.resolve();
    }
    if (dayjs(date).isBefore(now())) {
        return Promise.reject("This date is in the past");
    }
    return Promise.resolve();
};

export const VALIDATOR_NO_PAST_DATES = {
    validator: validateDateHasNotPassed,
    message: "Date must not be in the past.",
};

/**
 *
 * A More Robust Date picker form item, for any given date form name "x", this form item also contains
 * xTimezone and can be used just like any other form item. Very cool, very convienent.
 *
 * @returns JSX.Element (A datepicker with a hidden tz form item)
 */
export function RVEnhancedDateTimePickerFormItem({
    timezone = ZoneTZIndependant.et,
    onChange,
    name,
    showFormLabel,
    formItemHeader,
    label,
    className,
    rules,
    useUTCValue,
    disabledDate,
    disabledTime,
    ...rest
}: RVEnhancedDateTimePickerFormItemProps): JSX.Element {
    return (
        <>
            <RVFormFloatingLabelWrap
                formItemHeader={formItemHeader}
                label={label}
                name={name}
                className={className}
                showFormLabel={showFormLabel}
                rules={rules ?? []}
                {...rest}
            >
                <RVDateTimePicker
                    onChange={onChange}
                    timezone={timezone}
                    useUTCValue={useUTCValue}
                    disabledDate={disabledDate}
                    disabledTime={disabledTime}
                />
            </RVFormFloatingLabelWrap>
            <RVFormItem hidden name={`${name}Timezone`} initialValue={timezone}>
                <Input value={timezone}></Input>
            </RVFormItem>
        </>
    );
}
